package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.Quote
import com.example.positionahead.model.SavedPortfolio

@Composable
fun AddHubScreen(
    existingSymbols: List<String>,
    portfolios: List<SavedPortfolio>,
    activePortfolioId: String,
    initialSymbol: String?,
    initialDollarAmount: Double? = null,
    sellHintSymbol: String? = null,
    quotes: Map<String, Quote>,
    importErrors: List<String>,
    importSuccessCount: Int,
    onAddPurchase: (
        symbol: String,
        shares: Double,
        costBasis: Double,
        assetClass: AssetClass,
        purchaseDate: String?,
        note: String?,
        portfolioId: String?,
    ) -> Unit,
    onDone: () -> Unit,
    onImport: (String, replaceExisting: Boolean) -> Unit,
    onClearImportStatus: () -> Unit,
    onLookupQuote: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Text(
            "Add positions manually, size a purchase, or import a broker CSV.",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.75f),
        )

        sellHintSymbol?.let { symbol ->
            Card(
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.35f),
                ),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(
                    "Plan suggested selling $symbol — use the Portfolio tab, open that holding, and sell a lot for about that dollar amount.",
                    modifier = Modifier.padding(12.dp),
                    style = MaterialTheme.typography.bodySmall,
                )
            }
        }

        PositionSizerSection(
            quotes = quotes,
            portfolios = portfolios,
            activePortfolioId = activePortfolioId,
            initialSymbol = initialSymbol,
            initialDollarAmount = initialDollarAmount,
            onLookupQuote = onLookupQuote,
            onAddPurchase = onAddPurchase,
            onDone = onDone,
            modifier = Modifier.fillMaxWidth(),
        )

        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

        Text(
            "Add purchase",
            style = MaterialTheme.typography.titleMedium,
        )

        AddPositionScreen(
            existingSymbols = existingSymbols,
            portfolios = portfolios,
            activePortfolioId = activePortfolioId,
            initialSymbol = initialSymbol,
            onAddPurchase = onAddPurchase,
            onDone = onDone,
            embedded = true,
            modifier = Modifier.fillMaxWidth(),
        )

        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

        Text(
            "Import from broker",
            style = MaterialTheme.typography.titleMedium,
        )
        Text(
            "Upload a CSV export at the bottom of this section when bulk-importing.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )

        ImportScreen(
            importErrors = importErrors,
            importSuccessCount = importSuccessCount,
            onImport = onImport,
            onClearStatus = onClearImportStatus,
            embedded = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(24.dp))
    }
}
